import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AuthForm } from '../components/AuthForm';

interface DemoMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  steps?: Array<{
    description: string;
    status: 'complete' | 'running' | 'pending';
  }>;
}

export const LandingPage: React.FC = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [demoStarted, setDemoStarted] = useState(false);
  const [demoMessages, setDemoMessages] = useState<DemoMessage[]>([]);

  const startDemo = () => {
    setDemoStarted(true);
    const initialMessage: DemoMessage = {
      id: '1',
      role: 'user',
      content: 'I want to launch my SaaS product in 3 months'
    };

    setTimeout(() => {
      setDemoMessages([initialMessage]);

      setTimeout(() => {
        const response: DemoMessage = {
          id: '2',
          role: 'assistant',
          content: 'Perfect! Let me break down your SaaS launch into trackable milestones. I\'ll create a systematic plan to ensure you ship on time.',
          steps: [
            { description: 'Market research & validation', status: 'complete' },
            { description: 'MVP feature specification', status: 'complete' },
            { description: 'Technical architecture design', status: 'running' },
            { description: 'Development sprint planning', status: 'pending' },
            { description: 'Beta testing strategy', status: 'pending' }
          ]
        };
        setDemoMessages(prev => [...prev, response]);
      }, 1000);
    }, 500);
  };

  if (showAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-teal-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <button
              onClick={() => setShowAuth(false)}
              className="text-teal-600 hover:text-teal-700 text-sm mb-4 inline-flex items-center"
            >
              ← Back to landing page
            </button>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Welcome to GoalYet</h2>
            <p className="text-gray-600">Start your relentless progress today</p>
          </div>
          <AuthForm />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-teal-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-400/10 to-teal-400/10"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                🎯 Your Relentless AI Goal Assistant
              </div>

              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Turn Dreams Into
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-teal-600"> Progress</span>
              </h1>

              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                GoalYet breaks down any goal into trackable steps, provides intelligent guidance,
                and ensures you make <strong>deliberate progress</strong> every single day.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setShowAuth(true)}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-orange-600 hover:to-orange-700 transition-all transform hover:scale-105 shadow-lg"
                >
                  Start Your Journey
                </button>
                <button
                  onClick={startDemo}
                  className="border-2 border-teal-500 text-teal-600 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-teal-50 transition-all"
                >
                  See Demo
                </button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              {!demoStarted ? (
                <div className="bg-white rounded-2xl shadow-2xl p-8 border border-orange-100">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-orange-400 to-teal-500 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                      <span className="text-2xl">🤖</span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Ready to help you succeed</h3>
                    <p className="text-gray-600">Click "See Demo" to watch GoalYet in action</p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-3 bg-orange-50 rounded-lg">
                      <span className="text-orange-500">📋</span>
                      <span className="text-gray-700">Break down complex goals</span>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-teal-50 rounded-lg">
                      <span className="text-teal-500">📊</span>
                      <span className="text-gray-700">Track real progress</span>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-orange-50 rounded-lg">
                      <span className="text-orange-500">🎯</span>
                      <span className="text-gray-700">Never lose momentum</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-orange-100">
                  <div className="bg-gradient-to-r from-orange-400 to-teal-500 text-white p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-xl">🤖</span>
                        <span className="font-semibold">GoalYet AI</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse"></div>
                        <span className="text-sm">Demo</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 space-y-4 max-h-80 overflow-y-auto">
                    {demoMessages.map((message) => (
                      <motion.div
                        key={message.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-xs ${
                          message.role === 'user'
                            ? 'bg-orange-500 text-white'
                            : 'bg-gray-100 text-gray-800'
                        } rounded-lg p-3`}>
                          <p className="text-sm">{message.content}</p>

                          {message.steps && (
                            <div className="mt-3 space-y-1">
                              {message.steps.map((step, idx) => (
                                <div key={idx} className="flex items-center space-x-2">
                                  <span className="text-xs">
                                    {step.status === 'complete' ? '✅' :
                                     step.status === 'running' ? '⚡' : '⏳'}
                                  </span>
                                  <span className="text-xs text-gray-600">
                                    {step.description}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Goals Fail (And How We Fix It)
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Most goals fail because they're too vague, too big, or lack accountability.
              GoalYet transforms any aspiration into a systematic success plan.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center p-8 rounded-2xl bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200"
            >
              <div className="w-16 h-16 bg-orange-500 rounded-2xl mx-auto mb-6 flex items-center justify-center">
                <span className="text-2xl">🧩</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Break It Down</h3>
              <p className="text-gray-600">
                Every complex goal becomes a series of simple, actionable steps.
                No more overwhelming objectives—just clear next actions.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center p-8 rounded-2xl bg-gradient-to-br from-teal-50 to-teal-100 border border-teal-200"
            >
              <div className="w-16 h-16 bg-teal-500 rounded-2xl mx-auto mb-6 flex items-center justify-center">
                <span className="text-2xl">📈</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Track Progress</h3>
              <p className="text-gray-600">
                Visual progress tracking keeps you motivated and on course.
                See exactly how far you've come and what's next.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center p-8 rounded-2xl bg-gradient-to-br from-orange-50 to-teal-50 border border-gray-200"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-teal-500 rounded-2xl mx-auto mb-6 flex items-center justify-center">
                <span className="text-2xl">🤖</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">AI Guidance</h3>
              <p className="text-gray-600">
                Your relentless AI assistant provides personalized strategies,
                overcomes obstacles, and ensures consistent momentum.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 bg-gradient-to-r from-orange-100 to-teal-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              The GoalYet Promise
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whether you're building a business, learning a skill, or transforming your life—
              GoalYet ensures you make deliberate progress. Not someday. <strong>Yet.</strong>
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              { icon: '🚀', title: 'Launch a Business', desc: 'From idea to revenue' },
              { icon: '💪', title: 'Get Fit & Healthy', desc: 'Sustainable lifestyle changes' },
              { icon: '📚', title: 'Master New Skills', desc: 'Structured learning paths' },
              { icon: '💰', title: 'Achieve Financial Goals', desc: 'Smart money strategies' }
            ].map((goal, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-lg text-center border border-gray-100"
              >
                <div className="text-3xl mb-3">{goal.icon}</div>
                <h3 className="font-semibold text-gray-900 mb-2">{goal.title}</h3>
                <p className="text-sm text-gray-600">{goal.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={() => setShowAuth(true)}
              className="bg-gradient-to-r from-orange-500 to-teal-500 text-white px-10 py-4 rounded-xl font-semibold text-lg hover:from-orange-600 hover:to-teal-600 transition-all transform hover:scale-105 shadow-lg"
            >
              Start Making Progress Today
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};